import React, { useEffect, useRef, useState } from "react";
import Confetti from "react-confetti";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserBadgeEntry } from "interfaces/badge.interface";
import {
  getUserBadgeEntries,
  updateUserBadgeEntry,
} from "services/badges.service";

// Badge icons
const curiousIcon = "/assets/svg/badge-curious.svg";
const informedIcon = "/assets/svg/badge-informed.svg";
const empoweredIcon = "/assets/svg/badge-empowered.svg";

const badgeIcons: Record<string, string> = {
  curious: curiousIcon,
  informed: informedIcon,
  empowered: empoweredIcon,
};

const BadgePopup = () => {
  const queryClient = useQueryClient();

  const [badgeToShow, setBadgeToShow] = useState<UserBadgeEntry | null>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const popupRef = useRef<HTMLDivElement>(null);

  const { data: badgeEntries } = useQuery({
    queryKey: ["my-earned-badges"],
    queryFn: getUserBadgeEntries,
    staleTime: 0,
    enabled: true,
  });

  // Check for unnotified badge in badge list (reactive)
  useEffect(() => {
    if (badgeEntries?.length) {
      const unnotified = badgeEntries.find((b: any) => !b.user_notified);
      if (unnotified) {
        setBadgeToShow(unnotified);
      }
    }
  }, [badgeEntries]);

  // Resize confetti on window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close popup on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [badgeToShow]);

  const handleClose = async () => {
    if (badgeToShow) {
      await updateUserBadgeEntry(badgeToShow.id, {
        display_confetti: true,
        user_notified: true,
      });
      setBadgeToShow(null);
      await queryClient.invalidateQueries({ queryKey: ["my-earned-badges"] });
      // Refresh badge list
    }
  };

  if (!badgeToShow) return null;

  const badgeSlug = badgeToShow.badge.slug.toLowerCase();
  const iconSrc = badgeIcons[badgeSlug] || null;

  return (
    <>
      <style>
        {`
          @keyframes zoomInCenter {
            0% { transform: scale(0.8); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>

      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
        <Confetti width={windowSize.width} height={windowSize.height} />
        <div
          ref={popupRef}
          style={{ animation: "zoomInCenter 0.3s ease-out" }}
          className="relative bg-white p-4 rounded-lg shadow-lg text-center max-w-xs w-full"
        >
          {iconSrc ? (
            <img
              src={iconSrc}
              alt={badgeToShow.badge.title}
              className="mx-auto mb-3 w-20 h-20 object-contain"
            />
          ) : (
            <div className="text-4xl mb-3 select-none">🏅</div>
          )}

          <button
            onClick={handleClose}
            className="absolute top-0 right-2 text-gray-400 hover:text-gray-700 text-lg"
            aria-label="Close"
          >
            &times;
          </button>

          <h4 className="font-semibold text-lg mb-1">🎉 Badge Earned!</h4>
          <p className="text-gray-700 text-base">
            You earned the <strong>{badgeToShow.badge.title}</strong> badge!
          </p>
        </div>
      </div>
    </>
  );
};

export default BadgePopup;
